'use client'
import type { LoyaltyVoucherListProps } from '@maestro/commerce-blocks/loyalty/components/loyalty-voucher-list/loyalty-voucher-list.types'
import type { SelectedLoyaltyVoucher } from '@maestro/data-layer'

import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useNotificationContext } from '@maestro/commerce-blocks/components/notification/notification.context'
import { useCartContext } from '@maestro/commerce-blocks/context/cart.context'
import { getAppliedLoyaltyVouchers } from '@maestro/commerce-blocks/loyalty/client/api/loyalty'
import {
  applyLoyaltyVoucherToCart,
  removeLoyaltyVoucherFromCart,
} from '@maestro/commerce-blocks/loyalty/server/actions/loyalty-vouchers'

export const useLoyaltyVoucherList = (props: LoyaltyVoucherListProps) => {
  const t = useTranslations('commerce-blocks.loyalty')
  const { vouchers: vouchersProps } = props
  const { cart } = useCartContext()
  const { showSuccess, showError } = useNotificationContext()
  const [isLoading, setIsLoading] = useState(false)
  const [appliedVouchers, setAppliedVouchers] = useState<
    SelectedLoyaltyVoucher[]
  >([])

  useEffect(() => {
    if (cart.cartId) {
      setIsLoading(true)
      getAppliedLoyaltyVouchers(cart.cartId)
        .then((result) => {
          setAppliedVouchers(result)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [cart.cartId])

  const vouchers = useMemo(() => {
    return vouchersProps.map((voucher) => ({
      ...voucher,
      isSelected: appliedVouchers.some(
        (appliedVoucher) => appliedVoucher.id === voucher.id
      ),
    }))
  }, [appliedVouchers, vouchersProps])

  const removeSelectedVoucher = useCallback(
    async (voucherId: string) => {
      // Update optimistically
      setAppliedVouchers((prev) =>
        prev.filter((voucher) => voucher.id !== voucherId)
      )

      setIsLoading(true)

      const revertOptimisticUpdate = () => {
        const voucherToReapply = vouchersProps.find(
          (voucher) => voucher.id === voucherId
        )
        if (voucherToReapply) {
          setAppliedVouchers((prev) => [...prev, voucherToReapply])
        }
      }

      try {
        const response = await removeLoyaltyVoucherFromCart(
          cart.cartId,
          voucherId
        )
        if (!response.success) {
          revertOptimisticUpdate()
          if (response.errorType === 'general') {
            showError(
              response.errorMessage.title,
              response.errorMessage.description
            )
          } else {
            showError(t('messages.remove-failed'))
          }
          return
        }
        showSuccess(t('messages.removed-successfully'))
      } catch (_error) {
        revertOptimisticUpdate()
        showError(t('messages.remove-failed'))
      } finally {
        setIsLoading(false)
      }
    },
    [cart.cartId, showError, showSuccess, t, vouchersProps]
  )

  const applySelectedVoucher = useCallback(
    async (voucherId: string) => {
      // Update optimistically
      const voucherToApply = vouchersProps.find(
        (voucher) => voucher.id === voucherId
      )
      if (voucherToApply) {
        setAppliedVouchers((prev) => [...prev, voucherToApply])
      }

      setIsLoading(true)

      const revertOptimisticUpdate = () => {
        setAppliedVouchers((prev) =>
          prev.filter((voucher) => voucher.id !== voucherId)
        )
      }

      try {
        const response = await applyLoyaltyVoucherToCart(cart.cartId, voucherId)
        if (!response.success) {
          revertOptimisticUpdate()
          if (response.errorType === 'general') {
            showError(
              response.errorMessage.title,
              response.errorMessage.description
            )
          } else {
            showError(t('messages.apply-failed'))
          }
          return
        }
        showSuccess(t('messages.applied-successfully'))
      } catch (_error) {
        revertOptimisticUpdate()
        showError(t('messages.apply-failed'))
      } finally {
        setIsLoading(false)
      }
    },
    [cart.cartId, showError, showSuccess, t, vouchersProps]
  )

  const handleChange = useCallback(
    (voucherId: string) => {
      const isSelected = appliedVouchers.some(
        (voucher) => voucher.id === voucherId
      )
      if (isSelected) {
        removeSelectedVoucher(voucherId)
      } else {
        applySelectedVoucher(voucherId)
      }
    },
    [appliedVouchers, applySelectedVoucher, removeSelectedVoucher]
  )

  return {
    isLoading,
    vouchers,
    handleChange,
  }
}
